import React, { useEffect, useRef } from 'react';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBdQVJt98BquYXRn0ThYWilUXq4lnMOoJ8';
let loadPromise = null;

function loadGoogleMaps() {
  if (window.google?.maps?.places) return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-google-maps]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = 'true';
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return loadPromise;
}

function part(components, type, short = false) {
  const found = components.find(c => c.types.includes(type));
  return found ? (short ? found.short_name : found.long_name) : '';
}

// Drop-in replacement for a plain address <input>. Renders identically, but
// once Google's Places script loads, attaches autocomplete suggestions to
// it. Picking a suggestion calls onPlaceSelected with the parsed pieces
// (address/city/province/postalCode/country) so the parent form can fill
// every related field at once, not just the single address line.
export default function AddressAutocomplete({ value, onChange, onPlaceSelected, placeholder, style, ...rest }) {
  const inputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let autocomplete;
    let listener;

    loadGoogleMaps().then(() => {
      if (cancelled || !inputRef.current || !window.google?.maps?.places) return;
      autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
      });
      listener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.address_components) return;
        const components = place.address_components;
        const streetNumber = part(components, 'street_number');
        const route = part(components, 'route');
        const line1 = [streetNumber, route].filter(Boolean).join(' ');
        const city = part(components, 'locality') || part(components, 'sublocality') || part(components, 'postal_town');
        const province = part(components, 'administrative_area_level_1', true);
        const postalCode = part(components, 'postal_code');
        const country = part(components, 'country');
        onPlaceSelected?.({
          address: line1 || place.formatted_address || '',
          city, province, postalCode, country
        });
      });
    }).catch(() => {
      // Script failed to load (e.g. network blocked, invalid key) — the
      // field just stays a plain text input, no autocomplete but still
      // fully usable via the manual entry fields.
    });

    return () => {
      cancelled = true;
      if (listener) window.google?.maps?.event.removeListener(listener);
    };
  }, []);

  return (
    <input
      ref={inputRef}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || 'Start typing an address...'}
      style={style}
      {...rest}
    />
  );
}
